/**
 * medication/api — 복용 도메인 axios 호출 헬퍼.
 *
 * 백엔드 라우트는 모두 /api/v1/{patient|guardian|medication|prescription|schedule|intake|pocket-device|pocket-state|alert|iot|sim|ai} 형태.
 * 프레임워크 표준 axios 인스턴스(`@/framework` 의 `http`)를 사용한다.
 * 토큰 자동 부착 + 401 refresh 재시도 + 403/5xx 토스트 + 지수 백오프 재시도가 인터셉터에
 * 내장돼 있다. baseURL 이 `/api/v1` 이므로 아래 경로에는 prefix 를 쓰지 않는다.
 *
 * (2026-07-12: legacy `@/api/client` 에서 이관. 그 파일의 '현행 http 에는 refresh 가 없다'는
 *  주석은 사실이 아니었다 — framework/api/axios.ts 는 refresh 를 갖고 있다. 차이는 baseURL 뿐이었다.)
 *
 * 응답 타입은 `./types` 에 모아 두었다 — 백엔드에 응답 스키마가 없어(raw DB dict 반환)
 * 각 라우터의 SELECT 컬럼과 DDL CHECK 제약에서 도출한 것이다. 근거는 types.ts 헤더 참조.
 */
import { http as inst } from '@/framework'
import type {
  Alert,
  AlertType,
  ChatMessage,
  ChatResponse,
  ChatSession,
  CronMissedAck,
  DeviceBindAck,
  DeviceByPatient,
  DeviceResetAck,
  GuardianCandidate,
  GuardianInvite,
  GuardianInviteCreated,
  GuardianListItem,
  IntakeLog,
  IntakeStatus,
  IntakeWeeklyStats,
  IotTopics,
  ManualIntakeResult,
  Medication,
  MedicationCatalogItem,
  MedicationDetail,
  MyPatient,
  PairBootstrapAck,
  PairInfo,
  Patient,
  PatientCreatedAck,
  PatientDetail,
  PatientIdAck,
  PatientScreenResult,
  PocketDevice,
  PocketDeviceListItem,
  PocketStateResponse,
  PrescriptionConfirmResult,
  PrescriptionDetail,
  PrescriptionListItem,
  RefillLog,
  ReqBody,
  Schedule,
  ScheduleCreatedAck,
  ScreenResult,
  SeedDemoAck,
  SimState,
  SimToggleResult,
  TodaySchedule,
  UndoIntakeResult,
  VisionExtractResult,
  WeeklyReportDetail,
  WeeklyReportGenerated,
  WeeklyReportListItem
} from './types'

/**
 * 서버 응답 봉투 — `{success, data, meta}`.
 * 주의: 총 건수는 최상위 `total` 이 아니라 **`meta.total`** 이다
 * (backend `cm_crud.py` `_ok(data, **meta)`).
 */
interface Envelope<T> {
  /** 성공 여부 */
  success: boolean
  /** 본문 */
  data: T
  /** 부가 정보 — 목록 API 의 총 건수 등 */
  meta?: { total?: number }
  /** 실패 시 에러 */
  error?: { code?: string; message?: string }
}

/** 응답 본문에서 data 만 꺼내 반환. error 면 throw. */
function unwrap<T>(p: Promise<{ data: Envelope<T> }>): Promise<T> {
  return p.then((r) => {
    if (r.data?.success === false) throw new Error(r.data?.error?.message ?? 'API_ERROR')
    return r.data.data
  })
}

/** 삭제 ACK — `{<pk>: number, deleted: true}` 형태가 도메인마다 반복된다. */
type DeletedAck = Record<string, number | boolean>

export const medApi = {
  // ── patient ──
  patients: {
    list: (status?: string) =>
      unwrap<Patient[]>(inst.get('/patient', { params: status ? { status } : {} })),
    get: (code: string) => unwrap<PatientDetail>(inst.get(`/patient/${encodeURIComponent(code)}`)),
    create: (body: ReqBody) => unwrap<PatientCreatedAck>(inst.post('/patient', body)),
    update: (id: number, body: ReqBody) => unwrap<PatientIdAck>(inst.put(`/patient/${id}`, body)),
    remove: (id: number) => unwrap<DeletedAck>(inst.delete(`/patient/${id}`)),
    addGuardian: (id: number, body: ReqBody) =>
      unwrap<DeletedAck>(inst.post(`/patient/${id}/guardian`, body)),
    removeGuardian: (id: number, uid: number) =>
      unwrap<DeletedAck>(inst.delete(`/patient/${id}/guardian/${uid}`)),
    // 보호자 초대 코드 (발급/목록/취소 — ADMIN/MANAGER)
    invites: (id: number) => unwrap<GuardianInvite[]>(inst.get(`/patient/${id}/invite`)),
    inviteCreate: (id: number, body: ReqBody) =>
      unwrap<GuardianInviteCreated>(inst.post(`/patient/${id}/invite`, body)),
    inviteRevoke: (id: number, inviteId: number) =>
      unwrap<DeletedAck>(inst.delete(`/patient/${id}/invite/${inviteId}`))
  },
  // ── guardian ──
  guardians: {
    list: (patient_id?: number) =>
      unwrap<GuardianListItem[]>(
        inst.get('/guardian', { params: patient_id != null ? { patient_id } : {} })
      ),
    myPatients: () => unwrap<MyPatient[]>(inst.get('/guardian/me/patients')),
    updatePolicy: (body: ReqBody) => unwrap<DeletedAck>(inst.put('/guardian/policy', body)),
    // 관리자가 타인 매핑 정책 변경 (본인 전용 updatePolicy 와 구분)
    updatePolicyAdmin: (patient_id: number, user_id: number, body: ReqBody) =>
      unwrap<DeletedAck>(inst.put(`/patient/${patient_id}/guardian/${user_id}`, body)),
    // 보호자 후보 사용자 검색 (셀렉터용)
    candidates: (q: string) =>
      unwrap<GuardianCandidate[]>(inst.get('/guardian/candidates', { params: { q } })),
    // 주보호자 권한 이양 — 현재 주보호자/staff → 기존 보호자
    transferPrimary: (patient_id: number, to_user_id: number) =>
      unwrap<DeletedAck>(inst.post('/guardian/transfer-primary', { patient_id, to_user_id }))
  },
  // ── medication catalog ──
  medications: {
    list: (q?: string, risk_level?: string) =>
      unwrap<Medication[]>(
        inst.get('/medication', {
          params: { ...(q ? { q } : {}), ...(risk_level ? { risk_level } : {}) }
        })
      ),
    // 식약처 허가 카탈로그(43k) 제품명 검색 — 처방 약 선택용
    catalog: (q: string, limit = 30) =>
      unwrap<MedicationCatalogItem[]>(inst.get('/medication/catalog', { params: { q, limit } })),
    // 카탈로그 item_seq → tb_medication lazy materialize (멱등) — 마스터 등록용
    resolve: (item_seq: string) =>
      unwrap<MedicationDetail>(inst.post('/medication/resolve', { item_seq })),
    get: (code: string) =>
      unwrap<MedicationDetail>(inst.get(`/medication/${encodeURIComponent(code)}`)),
    create: (body: ReqBody) => unwrap<DeletedAck>(inst.post('/medication', body)),
    update: (id: number, body: ReqBody) => unwrap<DeletedAck>(inst.put(`/medication/${id}`, body)),
    remove: (id: number) => unwrap<DeletedAck>(inst.delete(`/medication/${id}`))
  },
  // ── prescription ──
  prescriptions: {
    list: (patient_id?: number) =>
      unwrap<PrescriptionListItem[]>(
        inst.get('/prescription', { params: patient_id != null ? { patient_id } : {} })
      ),
    get: (id: number) => unwrap<PrescriptionDetail>(inst.get(`/prescription/${id}`)),
    confirm: (body: ReqBody) =>
      unwrap<PrescriptionConfirmResult>(inst.post('/prescription/confirm', body)),
    remove: (id: number) => unwrap<DeletedAck>(inst.delete(`/prescription/${id}`))
  },
  // ── schedule ──
  schedules: {
    list: (patient_id: number) =>
      unwrap<Schedule[]>(inst.get('/schedule', { params: { patient_id } })),
    today: (patient_id: number) =>
      unwrap<TodaySchedule[]>(inst.get('/schedule/today', { params: { patient_id } })),
    create: (body: ReqBody) => unwrap<ScheduleCreatedAck>(inst.post('/schedule', body)),
    update: (id: number, body: ReqBody) => unwrap<DeletedAck>(inst.put(`/schedule/${id}`, body)),
    remove: (id: number) => unwrap<DeletedAck>(inst.delete(`/schedule/${id}`))
  },
  // ── intake ──
  intakes: {
    list: (
      patient_id: number,
      opts: { date_from?: string; date_to?: string; status?: IntakeStatus } = {}
    ) => unwrap<IntakeLog[]>(inst.get('/intake', { params: { patient_id, ...opts } })),
    weeklyStats: (patient_id: number) =>
      unwrap<IntakeWeeklyStats>(inst.get('/intake/stats/weekly', { params: { patient_id } })),
    manual: (body: ReqBody) => unwrap<ManualIntakeResult>(inst.post('/intake/manual', body)),
    undo: (body: ReqBody) => unwrap<UndoIntakeResult>(inst.post('/intake/manual/undo', body))
  },
  // ── pocket device + state ──
  devices: {
    list: () => unwrap<PocketDeviceListItem[]>(inst.get('/pocket-device')),
    get: (code: string) =>
      unwrap<PocketDevice>(inst.get(`/pocket-device/${encodeURIComponent(code)}`)),
    create: (body: ReqBody) => unwrap<DeletedAck>(inst.post('/pocket-device', body)),
    update: (id: number, body: ReqBody) =>
      unwrap<DeletedAck>(inst.put(`/pocket-device/${id}`, body)),
    remove: (id: number) => unwrap<DeletedAck>(inst.delete(`/pocket-device/${id}`)),
    cmd: (code: string, body: ReqBody) =>
      unwrap<DeletedAck>(inst.post(`/pocket-device/${encodeURIComponent(code)}/cmd`, body)),
    pair: (id: number) => unwrap<DeletedAck>(inst.post(`/pocket-device/${id}/pair`)),
    byPatient: (patient_id: number) =>
      unwrap<DeviceByPatient>(inst.get(`/pocket-device/by-patient/${patient_id}`)),
    bind: (device_code: string, patient_id: number | null, force = false) =>
      unwrap<DeviceBindAck>(inst.put('/pocket-device/bind', { device_code, patient_id, force })),
    // QR 셀프등록 — 스캔 후 미배정 여부 확인 / 신규 환자 생성+주보호자+바인딩 원자
    pairInfo: (code: string) =>
      unwrap<PairInfo>(inst.get(`/pocket-device/pair-info/${encodeURIComponent(code)}`)),
    pairBootstrap: (body: { device_code: string; patient: ReqBody; relation?: string }) =>
      unwrap<PairBootstrapAck>(inst.post('/pocket-device/pair-bootstrap', body)),
    // 초기화(중고·양도) — 주보호자/staff. 매핑해제+환자분리+포켓비움 → 재 pair-bootstrap 가능
    reset: (code: string) =>
      unwrap<DeviceResetAck>(inst.post(`/pocket-device/${encodeURIComponent(code)}/reset`)),
    state: (code: string) =>
      unwrap<PocketStateResponse>(inst.get(`/pocket-state/${encodeURIComponent(code)}`)),
    refills: (code: string) =>
      unwrap<RefillLog[]>(inst.get(`/pocket-state/${encodeURIComponent(code)}/refills`))
  },
  // ── alert ──
  alerts: {
    inbox: (unreadOnly = false) =>
      unwrap<Alert[]>(inst.get('/alert/inbox', { params: { unread_only: unreadOnly } })),
    create: (body: ReqBody) =>
      unwrap<{ patient_id: number; alert_type: AlertType }>(inst.post('/alert', body)),
    markRead: (id: number) => unwrap<DeletedAck>(inst.post(`/alert/${id}/read`)),
    markAllRead: () => unwrap<DeletedAck>(inst.post('/alert/read-all'))
  },
  // ── iot ──
  iot: {
    topics: () => unwrap<IotTopics>(inst.get('/iot/topics'))
  },
  // ── sim ──
  sim: {
    toggle: (code: string, idx: number, body: ReqBody) =>
      unwrap<SimToggleResult>(
        inst.post(`/sim/device/${encodeURIComponent(code)}/pocket/${idx}/toggle`, body)
      ),
    refill: (code: string, body: ReqBody) =>
      unwrap<DeletedAck>(inst.post(`/sim/device/${encodeURIComponent(code)}/refill`, body)),
    reset: (code: string) =>
      unwrap<DeletedAck>(inst.post(`/sim/device/${encodeURIComponent(code)}/reset`)),
    // 데모 일정(2슬롯x7요일=14건) 생성 + 포켓 충전 → 등록 직후 3D 시뮬 즉시 가능 (멱등)
    seedDemo: (code: string) =>
      unwrap<SeedDemoAck>(inst.post(`/sim/device/${encodeURIComponent(code)}/seed-demo`)),
    state: (code: string) =>
      unwrap<SimState>(inst.get(`/sim/device/${encodeURIComponent(code)}/state`)),
    // 미복용 감지 즉시 실행 — 가상약달력 테스트용 (missed INSERT + 보호자 alert fan-out)
    cronMissed: () => unwrap<CronMissedAck>(inst.post('/sim/cron/missed'))
  },
  // ── ai ──
  ai: {
    chat: (body: ReqBody) => unwrap<ChatResponse>(inst.post('/ai/chat', body)),
    chatSessions: (patient_id?: number) =>
      unwrap<ChatSession[]>(
        inst.get('/ai/chat/sessions', { params: patient_id != null ? { patient_id } : {} })
      ),
    chatMessages: (sid: number) =>
      unwrap<ChatMessage[]>(inst.get(`/ai/chat/sessions/${sid}/messages`)),
    weeklyReport: (patient_id?: number) =>
      unwrap<WeeklyReportListItem[]>(
        inst.get('/ai/weekly-report', { params: patient_id != null ? { patient_id } : {} })
      ),
    weeklyReportGet: (id: number) =>
      unwrap<WeeklyReportDetail>(inst.get(`/ai/weekly-report/${id}`)),
    weeklyReportGenerate: (body: ReqBody) =>
      unwrap<WeeklyReportGenerated>(inst.post('/ai/weekly-report/generate', body)),
    visionExtract: (file: File | Blob, patient_id?: number) => {
      const form = new FormData()
      form.append('image', file)
      return unwrap<VisionExtractResult>(
        inst.post('/ai/vision/extract', form, {
          params: patient_id != null ? { patient_id } : {},
          headers: { 'Content-Type': 'multipart/form-data' }
        })
      )
    }
  },
  // ── 별책② 약물 상호작용·PIM 스크리닝 ──
  interaction: {
    patient: (patient_id: number, age?: number) =>
      unwrap<PatientScreenResult>(
        inst.get(`/interaction/patient/${patient_id}`, {
          params: age != null ? { age } : {}
        })
      ),
    screen: (item_seqs: string[], age?: number) =>
      unwrap<ScreenResult>(inst.post('/interaction/screen', { item_seqs, age }))
  },
  // ── 관리: AI provider(cloud/onprem) 런타임 설정 (ADMIN) ──
  adminAiConfig: {
    get: () => unwrap<Record<string, string>>(inst.get('/admin/ai-config')),
    update: (config: Record<string, string>) =>
      unwrap<{ updated: string[] }>(inst.patch('/admin/ai-config', { config }))
  }
}

export type ApiError = { code?: string; message: string }

/** axios 에러 등 임의의 throw 값에서 사용자 친화 메시지를 뽑는다. */
export function toErrMsg(e: unknown): string {
  const err = e as {
    response?: { data?: { error?: { message?: string }; detail?: { message?: string } | string } }
    message?: string
  }
  const body = err?.response?.data
  if (body?.error?.message) return body.error.message
  if (typeof body?.detail === 'object' && body.detail?.message) return body.detail.message
  if (typeof body?.detail === 'string') return body.detail
  return err?.message ?? 'API_ERROR'
}
